import React from 'react';

interface EditBookDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  book: any;
  editFormData: {
    title: string;
    subtitle: string;
    name: string;
    authors: string[];
    publisher: string;
    price: number;
    language: string;
    categories: string[];
    tags: string[];
  };
  onInputChange: (field: string, value: any) => void;
  onArrayFieldChange: (field: string, value: string) => void;
  onRemoveArrayItem: (field: string, index: number) => void;
  isSaving: boolean;
  hasChanges: () => boolean;
  error: string | null;
  successMessage: string | null;
}

const EditBookDialog: React.FC<EditBookDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  book,
  editFormData,
  onInputChange,
  onArrayFieldChange,
  onRemoveArrayItem,
  isSaving,
  hasChanges,
  error,
  successMessage
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[700px] shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Chỉnh sửa sách</h3>
          
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
              <div className="flex items-start">
                <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <span className="font-medium">Lỗi:</span>
                  <span className="ml-1">{error}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
              <div className="flex items-start">
                <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <span className="font-medium">Thành công:</span>
                  <span className="ml-1">{successMessage}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Form fields in 2-column layout: labels | inputs */}
            <div className="grid grid-cols-5 gap-4">
              {/* Labels Column */}
              <div className="col-span-2 space-y-4">
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Tên sách</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Phụ đề</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Tác giả</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Nhà xuất bản</div>
                <label htmlFor="book-price" className="text-sm font-medium text-gray-700 flex items-center h-10">Giá (VND)</label>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Ngôn ngữ</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Danh mục</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Tags</div>
              </div>

              {/* Inputs Column */}
              <div className="col-span-3 space-y-4">
                <div>
                  <input
                    type="text"
                    value={editFormData.title}
                    onChange={(e) => onInputChange('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={isSaving}
                  />
                  {/* Hidden field for API compatibility - some APIs expect 'name' instead of 'title' */}
                  <input
                    type="hidden"
                    value={editFormData.name}
                    onChange={(e) => onInputChange('name', e.target.value)}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={editFormData.subtitle}
                    onChange={(e) => onInputChange('subtitle', e.target.value)}
                    placeholder="Phụ đề sách (nếu có)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Nhập tác giả, phân cách bằng dấu phẩy"
                      value={editFormData.authors.join(', ')}
                      onChange={(e) => onArrayFieldChange('authors', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={isSaving}
                    />
                    {editFormData.authors.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {editFormData.authors.map((author, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {author}
                            <button
                              type="button"
                              onClick={() => onRemoveArrayItem('authors', index)}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                              disabled={isSaving}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <input
                    type="text"
                    value={editFormData.publisher}
                    onChange={(e) => onInputChange('publisher', e.target.value)}
                    placeholder="Tên nhà xuất bản"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <input
                    id="book-price"
                    type="number"
                    min="0"
                    step="1000"
                    inputMode="numeric"
                    value={editFormData.price}
                    onChange={(e) => onInputChange('price', e.target.value === '' ? 0 : Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={isSaving}
                    aria-describedby="book-price-help"
                  />
                  <p id="book-price-help" className="mt-1 text-xs text-gray-500">Nhập 0 nếu sách miễn phí.</p>
                </div>
                <div>
                  <select
                    value={editFormData.language}
                    onChange={(e) => onInputChange('language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={isSaving}
                  >
                    <option value="">Chọn ngôn ngữ</option>
                    <option value="vi">Tiếng Việt</option>
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="de">Deutsch</option>
                    <option value="es">Español</option>
                    <option value="zh">中文</option>
                    <option value="ja">日本語</option>
                    <option value="ko">한국어</option>
                  </select>
                </div>
                <div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Nhập danh mục, phân cách bằng dấu phẩy"
                      value={editFormData.categories.join(', ')}
                      onChange={(e) => onArrayFieldChange('categories', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={isSaving}
                    />
                    {editFormData.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {editFormData.categories.map((category, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                          >
                            {category}
                            <button
                              type="button"
                              onClick={() => onRemoveArrayItem('categories', index)}
                              className="ml-1 text-green-600 hover:text-green-800"
                              disabled={isSaving}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Nhập tags, phân cách bằng dấu phẩy"
                      value={editFormData.tags.join(', ')}
                      onChange={(e) => onArrayFieldChange('tags', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={isSaving}
                    />
                    {editFormData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => onRemoveArrayItem('tags', index)}
                          className="ml-1 text-purple-600 hover:text-purple-800"
                          disabled={isSaving}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSaving}
            >
              Hủy
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSaving || !hasChanges()}
            >
              {isSaving ? 'Đang lưu...' : 'Lưu'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBookDialog;
